import { ItsmeClient } from '@frontend/itsme/api';
import { Logger } from '@frontend/logger';
import { IUser } from '@frontend/user/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification } from '../../hooks';
import { useKioskInactivityTimeoutOverride } from '../../kiosk-idle-timer/kiosk-idle-timer-override.context';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { authenticateKioskUser } from '../utils/authenticate-kiosk-user';

export const ITSME_QR_INACTIVITY_TIMEOUT_SECONDS = 200;
export const ITSME_QR_INACTIVITY_SHOW_SECONDS = 180;

enum ItsmeMessage {
    USER_AUTHENTICATED = 'itsme_user_authenticated',
    USER_DISCOVERED = 'itsme_user_discovered',
    QR_CODE_UPDATED = 'itsme_qr_code_updated',
    NO_MATCHING_USER_FOUND = 'itsme_no_matching_user_found',
    TOO_MANY_MATCHING_USERS_FOUND = 'itsme_too_many_matching_users_found',
    USER_DISCOVERY_TIMEOUT = 'itsme_user_discovery_timeout',
    USER_DISCOVERY_FAILED = 'itsme_user_discovery_failed',
    USER_NO_MOBILE_NUMBER = 'itsme_user_no_mobile_number',
    AUTHENTICATION_REQUEST_FAILED = 'itsme_authentication_request_failed',
    CIBA_ACCESS_TOKEN_FAILED = 'itsme_ciba_access_token_failed',
    NO_USER_DATA = 'itsme_no_user_data'
}

export interface UseItsmeQrAuthenticationFlowOptions {
    id: string;
    enabled?: boolean;
    onUserAuthenticated: (user: IUser) => void | Promise<void>;
}

export interface ItsmeQrAuthenticationFlowViewProps {
    expired: boolean;
    authenticating: boolean;
    authenticated: boolean;
    qrCode: string | null;
    error: string | null;
}

export const useItsmeQrAuthenticationFlow = (options: UseItsmeQrAuthenticationFlowOptions): ItsmeQrAuthenticationFlowViewProps => {
    const { id, enabled = true, onUserAuthenticated } = options;
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigationState = useAppSelector((store) => store.navigation);
    const iotId = navigationState.iot?.id;
    const setInactivityTimeoutOverride = useKioskInactivityTimeoutOverride();
    const onUserAuthenticatedRef = useRef(onUserAuthenticated);

    const [expired, setExpired] = useState<boolean>(true);
    const [authenticating, setAuthenticating] = useState<boolean>(false);
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);
    const authenticatingRef = useRef<boolean>(false);

    useEffect(() => {
        onUserAuthenticatedRef.current = onUserAuthenticated;
    }, [onUserAuthenticated]);

    const discoveryFailedMessage = intl.formatMessage({
        id: 'wizard.itsmeQrAuthentication.discoveryFailed',
        defaultMessage: "We couldn't start the login process. Please contact support for assistance.",
        description: 'Error message shown when the itsme authentication discovery fails.'
    });

    const noMatchingUserFoundMessage = intl.formatMessage({
        id: 'wizard.itsmeQrAuthentication.noMatchingUserFound',
        defaultMessage: "We couldn't find your account. Please contact support for assistance.",
        description: 'Error message shown when no matching user is found for the scanned itsme account.'
    });

    const handleIdentification = useCallback(
        async (user: IUser, accountId: string, iotId: string): Promise<boolean> => {
            const success = await authenticateKioskUser(dispatch, accountId, iotId, user);
            if (!success) {
                Logger.error('Failed to authenticate user after itsme identification');
                return false;
            }

            await onUserAuthenticatedRef.current(user);
            return true;
        },
        [dispatch]
    );

    useIdentification({
        id,
        onNoMatchingIdentificationFound: enabled
            ? () => {
                  setError(noMatchingUserFoundMessage);
              }
            : undefined,
        onUserIdentified: enabled
            ? (user) => {
                  setQrCode(null);
                  setError(null);

                  if (!navigationState.iot) {
                      Logger.error('IoT information is missing in navigation state, cannot proceed with user identification.');
                      setError(discoveryFailedMessage);
                      return;
                  }

                  if (expirationTimerRef.current) {
                      clearTimeout(expirationTimerRef.current);
                      expirationTimerRef.current = null;
                  }

                  void handleIdentification(user, navigationState.iot.account_id, navigationState.iot.id);
              }
            : undefined,
        onItsmeMessage: enabled
            ? (message, data) => {
                  switch (message) {
                      case ItsmeMessage.USER_AUTHENTICATED:
                          if (expirationTimerRef.current) {
                              clearTimeout(expirationTimerRef.current);
                          }
                          setError(null);
                          setQrCode(null);
                          setAuthenticating(false);
                          authenticatingRef.current = false;
                          setAuthenticated(true);
                          break;
                      case ItsmeMessage.USER_DISCOVERED:
                          if (expirationTimerRef.current) {
                              clearTimeout(expirationTimerRef.current);
                          }
                          setError(null);
                          setQrCode(null);
                          setAuthenticating(true);
                          authenticatingRef.current = true;
                          break;
                      case ItsmeMessage.QR_CODE_UPDATED:
                          if (authenticatingRef.current) return;
                          setQrCode(data.qr_code);
                          setExpired(false);

                          if (expirationTimerRef.current) {
                              clearTimeout(expirationTimerRef.current);
                          }

                          if (data.expires_at) {
                              const expirationTime = new Date(data.expires_at).getTime();
                              const now = Date.now();
                              const msUntilExpiration = expirationTime - now;
                              if (msUntilExpiration > 0) {
                                  expirationTimerRef.current = setTimeout(() => {
                                      setExpired(true);
                                  }, msUntilExpiration);
                              } else {
                                  setExpired(true);
                              }
                          }
                          break;
                      case ItsmeMessage.NO_MATCHING_USER_FOUND:
                          setError(noMatchingUserFoundMessage);
                          break;
                      case ItsmeMessage.TOO_MANY_MATCHING_USERS_FOUND:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.tooManyMatchingUsersFound',
                                  defaultMessage: 'We found more than one account for your itsme login. Please contact support for assistance.',
                                  description: 'Error message shown when multiple matching users are found for the scanned itsme account.'
                              })
                          );
                          break;
                      case ItsmeMessage.USER_DISCOVERY_TIMEOUT:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.userDiscoveryTimeout',
                                  defaultMessage: 'Unable to verify your account. Please contact support for assistance.',
                                  description: 'Error message shown when the itsme user discovery times out.'
                              })
                          );
                          break;
                      case ItsmeMessage.USER_DISCOVERY_FAILED:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.userDiscoveryFailed',
                                  defaultMessage: "We couldn't verify your account. Please contact support for assistance.",
                                  description: 'Error message shown when the itsme user discovery fails.'
                              })
                          );
                          break;
                      case ItsmeMessage.USER_NO_MOBILE_NUMBER:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.userNoMobileNumber',
                                  defaultMessage: 'Your itsme account needs a phone number. Please add one in the itsme app.',
                                  description: 'Error message shown when the itsme account has no mobile number associated.'
                              })
                          );
                          break;
                      case ItsmeMessage.AUTHENTICATION_REQUEST_FAILED:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.authenticationRequestFailed',
                                  defaultMessage: "We couldn't verify your identity. Please contact support for assistance.",
                                  description: 'Error message shown when the itsme authentication request fails.'
                              })
                          );
                          break;
                      case ItsmeMessage.CIBA_ACCESS_TOKEN_FAILED:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.cibaAccessTokenFailed',
                                  defaultMessage: 'Login verification failed. Please contact support for assistance.',
                                  description: 'Error message shown when retrieving the CIBA access token fails.'
                              })
                          );
                          break;
                      case ItsmeMessage.NO_USER_DATA:
                          setError(
                              intl.formatMessage({
                                  id: 'wizard.itsmeQrAuthentication.noUserData',
                                  defaultMessage: "We couldn't retrieve your information. Please contact support for assistance.",
                                  description: 'Error message shown when no user data is received from itsme.'
                              })
                          );
                          break;
                      default:
                          break;
                  }
              }
            : undefined
    });

    useEffect(() => {
        if (!enabled) return;
        return setInactivityTimeoutOverride({ idleSeconds: ITSME_QR_INACTIVITY_TIMEOUT_SECONDS, modalSeconds: ITSME_QR_INACTIVITY_SHOW_SECONDS });
    }, [enabled, setInactivityTimeoutOverride]);

    useEffect(() => {
        if (!enabled || !iotId) return;

        ItsmeClient.discover().catch(() => {
            setError(discoveryFailedMessage);
        });

        return () => {
            if (expirationTimerRef.current) {
                clearTimeout(expirationTimerRef.current);
            }
        };
    }, [enabled, iotId, discoveryFailedMessage]);

    return { expired, authenticating, authenticated, qrCode, error };
};
